import {
  getConnectionByInteraction,
  getParentRole,
  getActivity,
  getThreadByState,
} from "./radUtils.js";

export const TARGET_SOURCE_CLASSES = new Set([
  "role",
  "state",
  "connection",
  "activity",
  "case",
  "part",
  "repeat",
]);

export const sortUniqueIds = (ids) =>
  [...new Set((ids || []).filter(Boolean).map((id) => String(id)))].sort();

export const collectCandidateTargetIds = (rad) => {
  const roleIds = (rad.roles ?? []).map((role) => role.id);
  const stateIds = (rad.states ?? []).map((state) => state.id);
  const interactionIds = (rad.activities ?? [])
    .filter((activity) => activity.type === "interaction")
    .map((activity) => activity.id);
  const subthreadIds = (rad.threads ?? [])
    .filter((thread) => ["case", "part", "repeat"].includes(thread.class))
    .map((thread) => thread.id);
  return sortUniqueIds([
    ...roleIds,
    ...stateIds,
    ...interactionIds,
    ...subthreadIds,
  ]);
};

export const normalizeTargetSource = (source) => {
  const src = source && typeof source === "object" ? source : {};
  const sourceClass = src.className || src.class;
  return {
    id: typeof src.id === "string" ? src.id : "",
    class: TARGET_SOURCE_CLASSES.has(sourceClass) ? sourceClass : "",
    type: typeof src.type === "string" ? src.type : "",
  };
};

export const hydrateThreadSource = (rad, source) => {
  if (!["case", "part", "repeat"].includes(source.class)) {
    return source;
  }
  const thread = (rad.threads ?? []).find(
    (candidate) => candidate.id === source.id && candidate.class === source.class,
  );
  return thread ? { ...thread, ...source } : source;
};

export const buildTargetIndex = (rad) => {
  const roleIds = new Set((rad.roles ?? []).map((role) => role.id));
  const stateIds = new Set((rad.states ?? []).map((state) => state.id));
  const interactionIds = new Set(
    (rad.activities ?? [])
      .filter((activity) => activity.type === "interaction")
      .map((activity) => activity.id),
  );
  const subthreadIds = new Set(
    (rad.threads ?? [])
      .filter((thread) => ["case", "part", "repeat"].includes(thread.class))
      .map((thread) => thread.id),
  );
  return { roleIds, stateIds, interactionIds, subthreadIds };
};

export const sourceExists = (rad, source) => {
  switch (source.class) {
    case "role":
      return (rad.roles ?? []).some((role) => role.id === source.id);
    case "state":
      return (rad.states ?? []).some((state) => state.id === source.id);
    case "connection":
      return (rad.connections ?? []).some(
        (connection) => connection.id === source.id,
      );
    case "activity":
      return (rad.activities ?? []).some((activity) => activity.id === source.id);
    case "case":
    case "part":
    case "repeat":
      return (rad.threads ?? []).some(
        (thread) => thread.id === source.id && thread.class === source.class,
      );
    default:
      return false;
  }
};

export const resolveSourceRoleId = (rad, source) => {
  if (!source?.id) return null;
  if (source.class === "role") {
    return source.id;
  }
  if (source.class === "state") {
    const stateThread = getThreadByState(rad)(source.id);
    return stateThread ? getParentRole(rad)(stateThread.id)?.id ?? null : null;
  }
  if (["case", "part", "repeat"].includes(source.class)) {
    return getParentRole(rad)(source.id)?.id ?? null;
  }
  if (source.class === "activity" || source.type === "interaction") {
    const activity = getActivity(rad)(source.id);
    return activity ? getParentRole(rad)(activity.thread_id)?.id ?? null : null;
  }
  return null;
};

export const resolveTargetRoleId = (rad, index, targetId) => {
  if (index.roleIds.has(targetId)) {
    return targetId;
  }
  if (index.stateIds.has(targetId)) {
    const stateThread = getThreadByState(rad)(targetId);
    return stateThread ? getParentRole(rad)(stateThread.id)?.id ?? null : null;
  }
  if (index.interactionIds.has(targetId)) {
    const activity = getActivity(rad)(targetId);
    return activity ? getParentRole(rad)(activity.thread_id)?.id ?? null : null;
  }
  if (index.subthreadIds.has(targetId)) {
    return getParentRole(rad)(targetId)?.id ?? null;
  }
  return null;
};

export const resolveConnectionScope = (rad, source) => {
  let connection = null;
  if (source.class === "connection") {
    connection = (rad.connections ?? []).find((con) => con.id === source.id) ?? null;
  } else if (source.type === "interaction") {
    connection = getConnectionByInteraction(rad)(source.id) ?? null;
  }
  if (!connection) return null;

  const interactionIds = new Set(connection.interactions ?? []);
  const roleIds = new Set(
    [...interactionIds]
      .map((interactionId) => {
        const interaction = getActivity(rad)(interactionId);
        return interaction ? getParentRole(rad)(interaction.thread_id)?.id : null;
      })
      .filter(Boolean),
  );
  return { interactionIds, roleIds };
};

export const classifyBlockedReason = (
  rad,
  index,
  source,
  targetId,
  sourceRoleId,
  connectionScope,
  stateDetails,
) => {
  if (targetId === source.id) {
    return "source_self";
  }
  if (source.class === "repeat") {
    return "unimplemented_repeat";
  }

  const targetRoleId = resolveTargetRoleId(rad, index, targetId);
  if (connectionScope) {
    if (connectionScope.interactionIds.has(targetId)) {
      return "connection_member";
    }
    if (targetRoleId && connectionScope.roleIds.has(targetRoleId)) {
      return "connection_role_scope";
    }
    return "excluded_by_connection_rules";
  }

  if (sourceRoleId && targetRoleId && sourceRoleId === targetRoleId) {
    return "same_role_scope";
  }
  if (
    ["case", "part"].includes(source.class) &&
    (index.subthreadIds.has(targetId) ||
      index.interactionIds.has(targetId) ||
      index.stateIds.has(targetId))
  ) {
    return "refinement_crossing_scope";
  }
  if (source.class === "state" && stateDetails) {
    if (stateDetails.connectedInteractions.includes(targetId)) {
      return "state_connection_scope";
    }
    if (stateDetails.untargetAncestors.includes(targetId)) {
      return "state_temporal_ancestor";
    }
    if (stateDetails.untargetSuccessors.includes(targetId)) {
      return "state_temporal_successor";
    }
  }
  return `excluded_by_${source.class}_rules`;
};
